import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { faThLarge } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { difference, partition, union } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { Nav, Tab } from 'react-bootstrap';
import { TStepItem, TStepLegsOption } from '../../../../api/endpoints/SubsetOption';
import { sortNumbers } from '../../../../utils/Array';
import { deriveSteps } from '../../../../utils/StepItem';
import Col from '../../../Common/Col';
import Row from '../../../Common/Row';
import Checkbox from '../../../Inputs/Checkbox/Checkbox';
import CheckboxWrap from "../../../Ui/CheckboxWrap";
import SelectAllControl from "../../SelectAllControl";
import StepsByDayAndIncrementControl from '../StepsByDayAndIncrementControl';

export const filterValuesByLeg = (leg: TStepItem, values: Array<number | string>) => {
  const from = parseInt(leg.from, 10);
  const to = parseInt(leg.to, 10);

  return values.filter((value: string | number) => {
    const castedValue = parseInt(value.toString(), 10);

    return castedValue >= from && castedValue <= to;
  });
};

const StepWithLegsControl = (props: {
  spec: TStepLegsOption,
  values: Array<string>,
  onChange: (values: Array<string>) => void;
}) => {
  const { spec, values, onChange } = props;

  const [selectedSteps, setSelectedSteps] = useState<Array<number>>(values.map(v => parseInt(v, 10)));

  useEffect(() => {
    onChange(sortNumbers(selectedSteps).map(v => v.toString()));
  }, [selectedSteps])

  const onDayOrIncrementChange = (leg: TStepItem) => (
    newValues: Array<number>,
    strategy: 'add' | 'remove' | 'overwrite'
  ) => {
    const [currentLegValues, otherLegsValues] = partition(
      selectedSteps,
      (v) => v >= parseInt(leg.from, 10) && v <= parseInt(leg.to, 10)
    );
    let valuesToUpdate = [];

    switch (strategy) {
      case 'add':
        valuesToUpdate = union(currentLegValues, newValues);
        break;
      case 'remove':
        valuesToUpdate = difference(currentLegValues, newValues);
        break;
      case 'overwrite':
        valuesToUpdate = newValues;
        break;
    }

    setSelectedSteps(union(valuesToUpdate, otherLegsValues));
  }

  const onStepCheckboxChange = (value: number, checked: boolean) => {
    setSelectedSteps(checked ? [...selectedSteps, value] : selectedSteps.filter(v => v !== value));
  };

  const onSelectAll = (leg: TStepItem) => (values: Array<number>) => {
    onDayOrIncrementChange(leg)(deriveSteps(leg), values.length === 0 ? 'remove' : 'add');
  }

  return (
    <Fragment>
      {
        spec.value.legs.map((leg, index) => {
          const legValuesPool = deriveSteps(leg);
          const legValues = filterValuesByLeg(leg, selectedSteps).map(v => parseInt(v.toString(), 10));

          return (
            <div key={leg.from} className="steps-selector">
              <Tab.Container
                defaultActiveKey="aggregated-steps"
                transition={false}
                id={`leg-${leg.from}`}
              >
                <Row>
                  <Col className="steps-selector-tabs">
                    <div className="steps-selector-tabs-label">Leg {index + 1}</div>
                    <Col className="selection-controls">
                      <SelectAllControl onChange={onSelectAll(leg)} values={legValuesPool} />
                    </Col>
                    <Nav variant="pills">
                      <Nav.Item>
                        <Nav.Link eventKey="aggregated-steps" title="Select by day">
                          <FontAwesomeIcon icon={faCalendar} />
                        </Nav.Link>
                      </Nav.Item>

                      <Nav.Item>
                        <Nav.Link eventKey="all-steps" title="Select by step">
                          <FontAwesomeIcon icon={faThLarge} />
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Col>
                </Row>

                <Row>
                  <Col className="steps-selector-content">
                    <Tab.Content>
                      <Tab.Pane eventKey="aggregated-steps">
                        <StepsByDayAndIncrementControl
                          values={legValues}
                          valuesPool={legValuesPool}
                          onChange={onDayOrIncrementChange(leg)}
                        />
                      </Tab.Pane>

                      <Tab.Pane eventKey="all-steps">
                        <CheckboxWrap>
                          {
                            legValuesPool.map(option => {
                              return <Checkbox
                                key={option}
                                label={option.toString()}
                                value={option}
                                checked={selectedSteps.includes(option)}
                                name="step-ranges"
                                onChange={(checked) => onStepCheckboxChange(option, checked)}
                              />;
                            })
                          }
                        </CheckboxWrap>
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            </div>
          );
        })
      }
    </Fragment>

  );
};

export default StepWithLegsControl;
